import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, Alert, CircularProgress, IconButton,
  MenuItem, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { API_ENDPOINTS } from '../config';
import { buildAnnouncementSegments } from '../utils/announcementLinks';

const ICON_OPTIONS = [
  { value: '', label: 'ללא אייקון' },
  { value: 'shipping', label: 'משלוח' },
  { value: 'mail', label: 'מייל / ניוזלטר' }
];

const ACTION_OPTIONS = [
  { value: '', label: 'ללא' },
  { value: 'newsletter', label: 'פתיחת ניוזלטר' }
];

function emptyLink() {
  return { linkText: '', url: '' };
}

function emptyForm() {
  return {
    text: '',
    textEn: '',
    links: [],
    icon: '',
    action: '',
    isActive: true,
    startDate: '',
    endDate: ''
  };
}

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function PreviewBar({ text, links, icon }) {
  const segments = useMemo(() => buildAnnouncementSegments(text, links), [text, links]);
  const Icon = icon === 'shipping'
    ? LocalShippingOutlinedIcon
    : icon === 'mail'
      ? MailOutlineIcon
      : null;

  return (
    <Box
      sx={{
        mt: 2,
        backgroundColor: 'var(--brand-orange, #d8472a)',
        color: '#fff',
        borderRadius: '12px 12px 0 0',
        height: 'clamp(1.5rem, 3vh, 1.75rem)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        direction: 'rtl',
        fontSize: '0.78rem',
        fontWeight: 600,
        lineHeight: 1,
        gap: 0.5,
        overflow: 'hidden'
      }}
    >
      {Icon ? <Icon sx={{ fontSize: '0.95rem' }} /> : null}
      <Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {segments.map((seg, i) => (
          seg.type === 'link' ? (
            <Box
              component="span"
              key={i}
              sx={{
                textDecoration: 'underline',
                textUnderlineOffset: '0.12em',
                mx: '0.05em'
              }}
            >
              {seg.value}
            </Box>
          ) : (
            <React.Fragment key={i}>{seg.value}</React.Fragment>
          )
        ))}
      </Box>
    </Box>
  );
}

export default function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_ENDPOINTS.announcements}/admin/all`, {
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || res.status === 403) {
        throw new Error('יש להתחבר מחדש כדי לנהל את פס ההודעות');
      }
      if (!res.ok || !data.success) throw new Error(data.message || 'טעינה נכשלה');
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setError(e.message || 'טעינת הודעות נכשלה');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
    setError('');
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      text: item.text || '',
      textEn: item.textEn || item.text_en || '',
      links: Array.isArray(item.links)
        ? item.links.map((l) => ({
            linkText: l.linkText || l.link_text || '',
            url: l.url || ''
          }))
        : [],
      icon: item.icon || '',
      action: item.action || '',
      isActive: item.isActive !== false && item.is_active !== false,
      startDate: toDatetimeLocal(item.startDate || item.start_date),
      endDate: toDatetimeLocal(item.endDate || item.end_date)
    });
    setDialogOpen(true);
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        text: form.text,
        textEn: form.textEn,
        links: form.links,
        icon: form.icon,
        action: form.action,
        isActive: form.isActive,
        startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
        endDate: form.endDate ? new Date(form.endDate).toISOString() : null
      };
      const url = editing
        ? `${API_ENDPOINTS.announcements}/admin/${editing.id}`
        : `${API_ENDPOINTS.announcements}/admin`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'שמירה נכשלה');
      setDialogOpen(false);
      setSuccess(editing ? 'ההודעה עודכנה' : 'ההודעה נוצרה');
      await load();
    } catch (e) {
      setError(e.message || 'שמירה נכשלה');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('למחוק את ההודעה?')) return;
    try {
      const res = await fetch(`${API_ENDPOINTS.announcements}/admin/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'מחיקה נכשלה');
      setSuccess('ההודעה נמחקה');
      await load();
    } catch (e) {
      setError(e.message || 'מחיקה נכשלה');
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await fetch(`${API_ENDPOINTS.announcements}/admin/${item.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !(item.isActive !== false && item.is_active !== false) })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'עדכון נכשל');
      await load();
    } catch (e) {
      setError(e.message || 'עדכון נכשל');
    }
  };

  const move = async (index, direction) => {
    const next = [...items];
    const j = index + direction;
    if (j < 0 || j >= next.length) return;
    const tmp = next[index];
    next[index] = next[j];
    next[j] = tmp;
    setItems(next);
    try {
      const ids = next.map((it) => it.id);
      const res = await fetch(`${API_ENDPOINTS.announcements}/admin/reorder`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'שינוי סדר נכשל');
      if (Array.isArray(data.items)) setItems(data.items);
      else await load();
    } catch (e) {
      setError(e.message || 'שינוי סדר נכשל');
      await load();
    }
  };

  const updateLink = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      links: prev.links.map((l, i) => (i === index ? { ...l, ...patch } : l))
    }));
  };

  const addLink = () => {
    setForm((prev) => ({ ...prev, links: [...prev.links, emptyLink()] }));
  };

  const removeLink = (index) => {
    setForm((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };

  return (
    <Box dir="rtl">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          פס הודעות
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ backgroundColor: '#d8472a', '&:hover': { backgroundColor: '#c03d24' } }}
        >
          הודעה חדשה
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">סדר</TableCell>
                <TableCell align="right">טקסט</TableCell>
                <TableCell align="right">קישורים</TableCell>
                <TableCell align="right">פעיל</TableCell>
                <TableCell align="right">תאריכים</TableCell>
                <TableCell align="left">פעולות</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    אין הודעות עדיין
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        disabled={index === 0}
                        onClick={() => move(index, -1)}
                        aria-label="העלה"
                      >
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={index === items.length - 1}
                        onClick={() => move(index, 1)}
                        aria-label="הורד"
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="right" sx={{ maxWidth: 320 }}>
                      {item.text}
                    </TableCell>
                    <TableCell align="right">
                      {(item.links || []).length || '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Switch
                        size="small"
                        checked={item.isActive !== false && item.is_active !== false}
                        onChange={() => toggleActive(item)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                      {(item.startDate || item.start_date || item.endDate || item.end_date)
                        ? `${toDatetimeLocal(item.startDate || item.start_date) || '—'} ← ${toDatetimeLocal(item.endDate || item.end_date) || '—'}`
                        : 'תמיד'}
                    </TableCell>
                    <TableCell align="left">
                      <IconButton size="small" onClick={() => openEdit(item)} aria-label="עריכה">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => remove(item.id)} aria-label="מחיקה">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'עריכת הודעה' : 'הודעה חדשה'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="משפט ההודעה (עברית)"
            value={form.text}
            onChange={(e) => setForm((p) => ({ ...p, text: e.target.value }))}
            fullWidth
            required
            inputProps={{ maxLength: 120 }}
            helperText={`${form.text.length}/120`}
            margin="dense"
          />
          <TextField
            label="משפט באנגלית (אופציונלי)"
            value={form.textEn}
            onChange={(e) => setForm((p) => ({ ...p, textEn: e.target.value }))}
            fullWidth
            inputProps={{ maxLength: 120 }}
            helperText={`${form.textEn.length}/120`}
            margin="dense"
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              select
              label="אייקון"
              value={form.icon}
              onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
              sx={{ minWidth: 160 }}
              margin="dense"
            >
              {ICON_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="פעולה מיוחדת"
              value={form.action}
              onChange={(e) => setForm((p) => ({ ...p, action: e.target.value }))}
              sx={{ minWidth: 180 }}
              margin="dense"
              helperText="פתיחת ניוזלטר לוחצת על כל ההודעה"
            >
              {ACTION_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'none'} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Box>

          <FormControlLabel
            control={(
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
            )}
            label="פעיל"
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="תאריך התחלה (אופציונלי)"
              type="datetime-local"
              value={form.startDate}
              onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              margin="dense"
            />
            <TextField
              label="תאריך סיום (אופציונלי)"
              type="datetime-local"
              value={form.endDate}
              onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              margin="dense"
            />
          </Box>

          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            קישורים
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            הזינו את המילים המדויקות מהמשפט שיהפכו לקישור. השאירו ריק כדי לקשר את כל המשפט.
            כתובת יכולה להיות נתיב פנימי (מתחיל ב־/), קישור חיצוני, mailto:, או המילה newsletter לפתיחת הניוזלטר.
          </Typography>

          {form.links.map((link, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.5,
                border: '1px solid rgba(0,0,0,0.12)',
                borderRadius: 1,
                mb: 1
              }}
            >
              <TextField
                label="מילים לקישור"
                value={link.linkText}
                onChange={(e) => updateLink(index, { linkText: e.target.value })}
                fullWidth
                size="small"
                helperText="חייב להתאים בדיוק למילים במשפט; ריק = כל המשפט"
              />
              <TextField
                label="כתובת URL"
                value={link.url}
                onChange={(e) => updateLink(index, { url: e.target.value })}
                fullWidth
                size="small"
                placeholder="/about או https://... או newsletter"
              />
              <Button size="small" color="error" onClick={() => removeLink(index)} sx={{ alignSelf: 'flex-start' }}>
                הסר קישור
              </Button>
            </Box>
          ))}

          <Button variant="outlined" onClick={addLink} sx={{ alignSelf: 'flex-start' }}>
            הוסף קישור
          </Button>

          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 600 }}>
            תצוגה מקדימה
          </Typography>
          <PreviewBar text={form.text} links={form.links} icon={form.icon} />
          <Box sx={{ height: 12, backgroundColor: 'rgba(245, 240, 227, 0.95)', borderRadius: '0 0 12px 12px' }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>ביטול</Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || !form.text.trim()}
            sx={{ backgroundColor: '#d8472a', '&:hover': { backgroundColor: '#c03d24' } }}
          >
            {saving ? 'שומר…' : 'שמירה'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
